import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { authManager } from "../../data/managers/AuthManager";
import { useAuth } from "../../data/providers/AuthProvider";
import { useSnackbar } from "../../data/providers/SnackbarProvider";

const APP_SESSION_KEY = "app_session_meta_v1";
const APP_SESSION_MAX_MS = 12 * 60 * 60 * 1000;
const APP_SESSION_WARN_MS = 15 * 60 * 1000;
const APP_SESSION_CHECK_INTERVAL_MS = 30 * 1000;

type StoredSession = {
  uid: string;
  startedAt: number;
};

function readStoredSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(APP_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    if (!parsed?.uid || !parsed?.startedAt) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredSession(data: StoredSession) {
  localStorage.setItem(APP_SESSION_KEY, JSON.stringify(data));
}

function clearStoredSession() {
  localStorage.removeItem(APP_SESSION_KEY);
}

export default function SessionGuardian() {
  const { user } = useAuth();
  const { showError } = useSnackbar();
  const navigate = useNavigate();
  const warnedRef = useRef(false);
  const logoutInProgressRef = useRef(false);

  useEffect(() => {
    if (!user) {
      clearStoredSession();
      warnedRef.current = false;
      logoutInProgressRef.current = false;
      return;
    }

    const ensureSession = () => {
      const current = readStoredSession();
      if (current?.uid === user.uid && current.startedAt > 0) return current;
      const next = { uid: user.uid, startedAt: Date.now() };
      writeStoredSession(next);
      warnedRef.current = false;
      return next;
    };

    const checkSession = async () => {
      const current = ensureSession();
      const expiresAt = current.startedAt + APP_SESSION_MAX_MS;
      const remainingMs = expiresAt - Date.now();

      if (remainingMs <= 0) {
        if (logoutInProgressRef.current) return;
        logoutInProgressRef.current = true;
        showError("Tu sesion expiro. Inicia sesion nuevamente.");
        await authManager.logout();
        navigate("/login?session=expired", { replace: true });
        return;
      }

      if (remainingMs <= APP_SESSION_WARN_MS && !warnedRef.current) {
        warnedRef.current = true;
        const minutesLeft = Math.max(1, Math.ceil(remainingMs / (60 * 1000)));
        showError(
          `Tu sesion expirara en ${minutesLeft} min. Guarda tus cambios para evitar perdida de informacion.`,
        );
      }
    };

    void checkSession();
    const timer = window.setInterval(() => {
      void checkSession();
    }, APP_SESSION_CHECK_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [navigate, showError, user?.uid]);

  return null;
}
